/**
 * Dates, as the product spells them — canvas turn 31, "the centralised theme".
 *
 * Turn 31's argument is that placements, searches, tasks and the pipeline all
 * need dates and each had been drawing its own, so the cell, the field, the
 * month and the range are named once and reused. This file is the arithmetic
 * half of that: everything the range panel and the day cell need, and nothing
 * that draws.
 *
 * Dates are `yyyy-mm-dd` strings throughout, never `Date` objects. Two reasons:
 * the placements filter already exchanges that shape with the API, and a
 * calendar date has no time zone — the moment one becomes a `Date` it acquires
 * the viewer's offset, and a range picked in Auckland stops matching the one
 * picked in Lisbon. As strings they also order lexically, so "is this day
 * inside the range" is a pair of string comparisons rather than three parses.
 */

/** A calendar date as `yyyy-mm-dd`. */
export type IsoDate = string

/** `month` is 0–11, matching `Date`. */
export type YearMonth = { year: number; month: number }

export type Day = {
  iso: IsoDate
  /** Day of the month, for the label. */
  date: number
  /**
   * False for the days borrowed from the neighbouring month. 31a keeps the
   * weeks whole rather than blanking them — "a cross-month range is the common
   * case" — so they are rendered, in the muted ink, and are pickable.
   */
  inMonth: boolean
}

const DAY = 86_400_000

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

const SHORT = MONTHS.map((month) => month.slice(0, 3))

/** Sunday first, as 31a draws it. */
export const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]

const pad = (value: number) => String(value).padStart(2, "0")

export function isoOf(year: number, month: number, date: number): IsoDate {
  return `${String(year).padStart(4, "0")}-${pad(month + 1)}-${pad(date)}`
}

/**
 * A date, or nothing.
 *
 * Strict on purpose: "2026-04-31" is not a day in April, and a picker that
 * silently rolls it into May would move a filter the user did not move.
 */
export function parseIso(iso?: string | null): YearMonth & { date: number } | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "")
  if (!match) return undefined

  const year = Number(match[1])
  const month = Number(match[2]) - 1
  const date = Number(match[3])
  if (month < 0 || month > 11 || date < 1) return undefined
  if (date > daysInMonth({ year, month })) return undefined

  return { year, month, date }
}

/** Today where the viewer is, not where the server is. */
export function todayIso(now = new Date()): IsoDate {
  return isoOf(now.getFullYear(), now.getMonth(), now.getDate())
}

/** The month a date sits in, or `fallback` when there is no date. */
export function monthOf(iso: string | undefined, fallback: YearMonth): YearMonth {
  const parsed = parseIso(iso)
  return parsed ? { year: parsed.year, month: parsed.month } : fallback
}

export function monthOfToday(now = new Date()): YearMonth {
  return { year: now.getFullYear(), month: now.getMonth() }
}

/** Normalised, so December + 1 is January of the next year rather than month 12. */
export function addMonths(ym: YearMonth, delta: number): YearMonth {
  const total = ym.year * 12 + ym.month + delta
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 }
}

export function monthLabel(ym: YearMonth) {
  return `${MONTHS[ym.month]} ${ym.year}`
}

function daysInMonth(ym: YearMonth) {
  return new Date(Date.UTC(ym.year, ym.month + 1, 0)).getUTCDate()
}

/**
 * The grid for one month: whole weeks, Sunday first, the neighbouring month's
 * days filling both ends.
 *
 * UTC throughout — the grid is a picture of a calendar, and building it from
 * local midnights puts a day either side of a DST boundary in the wrong cell.
 */
export function monthDays(ym: YearMonth): Day[] {
  const first = Date.UTC(ym.year, ym.month, 1)
  const lead = new Date(first).getUTCDay()
  const cells = Math.ceil((lead + daysInMonth(ym)) / 7) * 7

  const days: Day[] = []
  for (let index = 0; index < cells; index += 1) {
    const at = new Date(first + (index - lead) * DAY)
    days.push({
      iso: isoOf(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()),
      date: at.getUTCDate(),
      inMonth: at.getUTCMonth() === ym.month && at.getUTCFullYear() === ym.year,
    })
  }
  return days
}

/** "22 Apr 2026" — 31c's absolute form, for anything anyone might diarise. */
export function dayLabel(iso?: string): string | undefined {
  const parsed = parseIso(iso)
  if (!parsed) return undefined
  return `${parsed.date} ${SHORT[parsed.month]} ${parsed.year}`
}

/** "12 Apr 26" — the same date with 31a's two-digit year, for the chip. */
function shortDay(parsed: YearMonth & { date: number }, withYear: boolean) {
  const day = `${parsed.date} ${SHORT[parsed.month]}`
  return withYear ? `${day} ${String(parsed.year).slice(2)}` : day
}

/**
 * What the chip states — 31a's "12 Apr – 8 May 26".
 *
 * The year is written once when both ends share it, and on both ends when they
 * do not. A one-sided range is still a range the page can hold, so it gets a
 * word rather than a dash against nothing.
 */
export function rangeLabel(from?: string, to?: string): string {
  const start = parseIso(from)
  const end = parseIso(to)

  if (start && end) {
    const sameYear = start.year === end.year
    return `${shortDay(start, !sameYear)} – ${shortDay(end, true)}`
  }
  if (start) return `from ${shortDay(start, true)}`
  if (end) return `until ${shortDay(end, true)}`
  return "any"
}

/**
 * The two picks in order, so a range dragged backwards is still a range.
 *
 * Lexical comparison is the whole implementation: `yyyy-mm-dd` sorts as dates
 * do.
 */
export function orderRange(a: IsoDate, b: IsoDate): [IsoDate, IsoDate] {
  return a <= b ? [a, b] : [b, a]
}

/** Inclusive of both ends. An open end does not bound that side. */
export function withinRange(iso: IsoDate, from?: string, to?: string) {
  if (from && iso < from) return false
  if (to && iso > to) return false
  return Boolean(from || to)
}

/**
 * The same day a month away, clamped to the month it lands in.
 *
 * 31 January plus a month is 28 February, not 3 March: the panel's PageDown
 * moves the *view* by a month, and the focused day has to still be a day in
 * what the view is now showing or the keyboard has nowhere to stand.
 */
export function shiftMonths(iso: IsoDate, delta: number): IsoDate {
  const parsed = parseIso(iso)
  if (!parsed) return iso
  const ym = addMonths({ year: parsed.year, month: parsed.month }, delta)
  return isoOf(ym.year, ym.month, Math.min(parsed.date, daysInMonth(ym)))
}

/**
 * How each cell in a month draws, so the selection reads as one band.
 *
 * 31a: "the two bounds take the accent fill, everything between takes the wash,
 * and the band rounds at the range ends and at each row's edge". The rounding
 * is therefore a property of the cell's *neighbours*, not of the date, which is
 * why it is computed once over the whole grid rather than per cell.
 */
export type Band = {
  state: "plain" | "in" | "bound"
  roundLeft: boolean
  roundRight: boolean
}

export function bandShape(days: Day[], from?: string, to?: string): Band[] {
  const states = days.map((day): Band["state"] => {
    if ((from && day.iso === from) || (to && day.iso === to)) return "bound"
    if (from && to && day.iso > from && day.iso < to) return "in"
    return "plain"
  })

  return states.map((state, index) => {
    if (state === "bound") return { state, roundLeft: true, roundRight: true }
    if (state === "plain") return { state, roundLeft: false, roundRight: false }
    const column = index % 7
    return {
      state,
      roundLeft: column === 0 || states[index - 1] !== "in",
      roundRight: column === 6 || states[index + 1] !== "in",
    }
  })
}

/**
 * A day some number of days away, for arrow-key navigation.
 *
 * UTC arithmetic again: adding 86,400,000ms to a local midnight lands on the
 * same day twice a year.
 */
export function shiftDays(iso: IsoDate, delta: number): IsoDate {
  const parsed = parseIso(iso)
  if (!parsed) return iso
  const at = new Date(Date.UTC(parsed.year, parsed.month, parsed.date) + delta * DAY)
  return isoOf(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate())
}
